import { Fleet } from '../interfaces';

const DIRECTION_PREFIX = /^(NB|SB|EB|WB)\s+/i;

/**
 * Strip a leading direction (NB/SB/EB/WB) and uppercase, so block names coming
 * from the backend and the direction-prefixed keys in
 * `fleet.global_route_block_mapping` can be compared regardless of direction or
 * casing. The backend dedupes block names by exact string only, and mapping
 * keys are hand-entered, so both sides routinely differ in casing.
 */
export const normalizeBlockName = (name: string): string => name.trim().replace(DIRECTION_PREFIX, '').toUpperCase();

export interface BlockNameOption {
  blockName: string;
  translation?: string;
}

export interface BlockNameTranslator {
  /** One entry per location (deduped by normalized block name) with its translated name, for the datalist. */
  options: BlockNameOption[];
  /** Translated name for a stored block name, matched case/direction-insensitively; falls back to the block name. */
  displayName: (blockName: string) => string;
  /** Map a typed/selected translation back to its searchable block name (pass-through otherwise). */
  toBlockName: (input: string) => string;
}

/**
 * Pass-through translator for fleets without block name translation: the
 * datalist shows the endpoint block names verbatim (no dedup or reordering, as
 * before), and neither the selected names nor typed input are translated.
 */
export const buildPassthroughBlockNameTranslator = (blockNames: string[]): BlockNameTranslator => ({
  options: blockNames.map((blockName) => ({ blockName })),
  displayName: (blockName) => blockName,
  toBlockName: (input) => input,
});

/**
 * Build the translation helpers for the location filter from the fleet's
 * `global_route_block_mapping`. The mapping is keyed by event type (`*` or an
 * `EventTypeIdentifier`), then by direction-prefixed block name, e.g.
 * `{ '*': { 'NB Main St': 'Main Street North' } }`.
 */
export const buildBlockNameTranslator = (
  blockNames: string[],
  mapping: Fleet['global_route_block_mapping'],
): BlockNameTranslator => {
  const translationByNormalizedKey = new Map<string, string>();

  Object.values(mapping ?? {}).forEach((entries) => {
    Object.entries(entries ?? {}).forEach(([key, translation]) => {
      if (translation) {
        translationByNormalizedKey.set(normalizeBlockName(key), translation);
      }
    });
  });

  const blockNameByTranslation = new Map<string, string>();
  const translated: BlockNameOption[] = [];
  const untranslated: BlockNameOption[] = [];
  const seenNormalized = new Set<string>();

  blockNames.forEach((blockName) => {
    const normalized = normalizeBlockName(blockName);

    // Collapse block names that differ only by direction/casing (the endpoint
    // can return e.g. both "Main St" and "MAIN ST") so the datalist shows one.
    if (seenNormalized.has(normalized)) {
      return;
    }
    seenNormalized.add(normalized);

    const translation = translationByNormalizedKey.get(normalized);
    if (translation) {
      const key = translation.trim().toUpperCase();
      if (!blockNameByTranslation.has(key)) {
        blockNameByTranslation.set(key, blockName);
      }
      translated.push({ blockName, translation });
    } else {
      untranslated.push({ blockName, translation });
    }
  });

  // Translated names first, then the remaining endpoint block names.
  const options = [...translated, ...untranslated];

  return {
    options,
    displayName: (blockName) => translationByNormalizedKey.get(normalizeBlockName(blockName)) ?? blockName,
    toBlockName: (input) => blockNameByTranslation.get(input.trim().toUpperCase()) ?? input,
  };
};

/**
 * Append candidate block names to the current selection, trimming them and
 * skipping any that already exist case-insensitively (both against the current
 * selection and within the candidate list). The selection is a case-insensitive
 * set: "Filellinon @ Mitropoleos" and "FILELLINON @ MITROPOLEOS" are the same
 * block name and cannot both be selected. Direction is intentionally NOT
 * stripped here, so different directions remain distinct selections.
 */
export const appendUniqueBlockNames = (existing: string[], candidates: string[]): string[] => {
  const seen = new Set(existing.map((name) => name.trim().toUpperCase()));
  const result = [...existing];

  candidates.forEach((candidate) => {
    const name = candidate.trim();
    const key = name.toUpperCase();

    if (!name || seen.has(key)) {
      return;
    }

    seen.add(key);
    result.push(name);
  });

  return result;
};
